import { useReducer, useCallback } from "react";
import type { Fold, GamePhase, GameState, Puzzle } from "../types.ts";
import { applyFold } from "../engine/fold.ts";
import { matchesTarget } from "../engine/stack.ts";
import { calculateStars } from "../engine/scoring.ts";

type GameAction =
  | { type: "FOLD"; fold: Fold }
  | { type: "UNDO" }
  | { type: "RESET" }
  | { type: "NEW_PUZZLE"; puzzle: Puzzle };

function initGameState(puzzle: Puzzle): GameState {
  return {
    puzzle,
    currentGrid: puzzle.initialGrid,
    foldHistory: [],
    phase: "playing",
    startedAt: Date.now(),
    stars: 0,
  };
}

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "FOLD": {
      if (state.phase !== "playing") return state;

      const newGrid = applyFold(state.currentGrid, action.fold);
      const newHistory = [...state.foldHistory, action.fold];
      const won = matchesTarget(newGrid, state.puzzle.target);

      return {
        ...state,
        currentGrid: newGrid,
        foldHistory: newHistory,
        phase: won ? "won" : "playing",
        completedAt: won ? Date.now() : undefined,
        stars: won ? calculateStars(newHistory, state.puzzle) : 0,
      };
    }

    case "UNDO": {
      if (state.foldHistory.length === 0 || state.phase !== "playing")
        return state;

      const newHistory = state.foldHistory.slice(0, -1);
      let grid = state.puzzle.initialGrid;
      for (const fold of newHistory) {
        grid = applyFold(grid, fold);
      }
      return { ...state, currentGrid: grid, foldHistory: newHistory };
    }

    case "RESET":
      return {
        ...state,
        currentGrid: state.puzzle.initialGrid,
        foldHistory: [],
        phase: "playing",
        startedAt: Date.now(),
      };

    case "NEW_PUZZLE":
      return initGameState(action.puzzle);

    default:
      return state;
  }
}

export function useGame(puzzle: Puzzle) {
  const [state, dispatch] = useReducer(gameReducer, puzzle, initGameState);

  const doFold = useCallback(
    (fold: Fold) => dispatch({ type: "FOLD", fold }),
    []
  );
  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);
  const newPuzzle = useCallback(
    (p: Puzzle) => dispatch({ type: "NEW_PUZZLE", puzzle: p }),
    []
  );

  return { state, doFold, undo, reset, newPuzzle };
}
